import queryKeys from '@/constants/query-keys';
import { useSiteStore } from '@/store/site';
import { getAuthToken, useAuthToken, useAuthActions, getAuthActions } from '@/store/auth';
import { ApiQueryPromise } from '@/types';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { getResponseJwtHeader } from '@/utils/web';
import { ApiDomain } from '@nf/types/site';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { getFormData } from '@nf/utils-common/form';
import { itemsEmptyCheck } from '@nf/utils-common/fpw/utils';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useEffect } from 'react';
import { OddsService } from '@nf/odds/services';
import { jwtFetcher } from '@nf/utils-common/fetcher';
import { ShowAllOddsResponse, SportMatchesData } from '@nf/odds/types';
import { AccountActStatus } from '@nf/utils-common/constants';

export interface MyFavouritesInfo {
	LeagueLimit: number;
	TeamLimit: number;
	Ldata: MyFavouritesLeague[];
	Mdata: MyFavouritesMatch[];
	Teams: MyFavouritesTeam[];
}
export interface MyFavouritesStatus {
	leagueIds?: Array<number>;
	matchIds: Array<number>;
	teamIds?: Array<number>;
}
export interface MyFavouritesLeague {
	LeagueId: number;
	IsOutright: boolean;
	IsRunning: number;
	SportType: number;
}

export interface MyFavouritesMatch {
	MatchId: number;
	ExpireTime?: string;
	IsRunning: number;
	SportType: number;
}

export interface MyFavouritesTeam {
	TeamId: number;
	TeamName: string;
	IsRunning: number;
	SportType: number;
}

interface AddLeagueToMyFavouritesPayload {
	LeagueId: number;
	SportType: number;
}

interface RemoveLeagueFromMyFavouritesPayload {
	LeagueId: number;
	OnlyLeague: boolean;
}

interface AddMatchToMyFavouritesPayload {
	MatchId: number;
	Time: string;
	SportType: number;
}

interface RemoveMatchFromMyFavouritesPayload {
	MatchId: number;
}
const createApiUrl = (apiDomain: ApiDomain, path: MyFavouritePath) => cloudUrlParser(`${apiDomain.ctcdDomain}/Favorites/${path.toString()}`);

enum MyFavouritePath {
	GetAllInfo = 'GetFavorite',
	AddLeague = 'AddLeagueByApi',
	RemoveLeague = 'RemoveLeagueByApi',
	AddMatch = 'AddMatchByApi',
	RemoveMatch = 'RemoveMatchByApi',
	GetStatus = 'GetStatus'
}

export const fetchMyFavourites = async (apiDomain: ApiDomain): Promise<ApiQueryPromise<MyFavouritesInfo>> => {
	const authToken = getAuthToken();
	const { updateAuthToken } = getAuthActions();
	const apiUrl = createApiUrl(apiDomain, MyFavouritePath.GetAllInfo);
	const response = await fetchComposer.postWithBearer(apiUrl.toString())(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`, { cause: response });
	}
	const token = getResponseJwtHeader(response);
	if (token) {
		updateAuthToken(token);
	}
	return {
		data: await response.json(),
		jwtToken: token
	};
};

export const updateMyFavourite = async (apiDomain: ApiDomain, path: MyFavouritePath, data: Record<string, any>): Promise<ApiQueryPromise> => {
	const authToken = getAuthToken();
	const apiUrl = createApiUrl(apiDomain, path);
	const response = await fetchComposer.postWithBearerBase(
		apiUrl.toString(),
		{
			body: getFormData(data)
		}
	)(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`, { cause: response });
	}
	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
}

export const useMyFavourites = () => {
	const authToken = useAuthToken();
	const apiDomain = useSiteStore(state => state.apiDomain);
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.MY_FAVOURITE_INFO],
		queryFn: () => fetchMyFavourites(apiDomain),
		staleTime: 1000 * 60 * 5,
		cacheTime: 1000 * 60 * 10,
		refetchOnWindowFocus: false,
		enabled: Boolean(authToken),
	});

	return {
		data: data?.data?.Data,
		isLoading,
		isError,
		error,
		refetch
	}
}

type changeMyFavouriteAction<T> = (draft: MyFavouritesInfo) => (data: T) => void;

const handleMyFavouriteChange = <T>(queryClient: QueryClient, data: T, changeMyFavouriteAction: changeMyFavouriteAction<T>) => {
	queryClient.cancelQueries([queryKeys.MY_FAVOURITE_INFO]);
	queryClient.setQueryData<ApiQueryPromise<MyFavouritesInfo> | undefined>([queryKeys.MY_FAVOURITE_INFO], old => produce(old, draft => {
		if (!draft?.data.Data) return;
		changeMyFavouriteAction(draft.data.Data)(data);
	}));
}

export const useAddLeagueToMyFavourties = () => {
	const queryClient = useQueryClient();
	const { updateAuthToken } = useAuthActions();
	const apiDomain = useSiteStore(state => state.apiDomain);
	const mutation = useMutation({
		mutationFn: (addLeagueToMyFavouritesPayload: AddLeagueToMyFavouritesPayload) => updateMyFavourite(apiDomain, MyFavouritePath.AddLeague, addLeagueToMyFavouritesPayload),
		onSuccess(_, addLeagueToMyFavouritesPayload) {
			handleMyFavouriteChange(queryClient, addLeagueToMyFavouritesPayload, addLeagueToDraft);
		},
		onSettled(data) {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		},
	});
	return mutation;
}

export const useRemoveLeagueFromMyFavourites = () => {
	const queryClient = useQueryClient();
	const { updateAuthToken } = useAuthActions();
	const apiDomain = useSiteStore(state => state.apiDomain);
	const mutation = useMutation({
		mutationFn: (removeLeagueFromMyFavouritesPayload: RemoveLeagueFromMyFavouritesPayload) => updateMyFavourite(apiDomain, MyFavouritePath.RemoveLeague, removeLeagueFromMyFavouritesPayload),
		onSuccess(_, removeLeagueFromMyFavouritesPayload) {
			handleMyFavouriteChange(queryClient, removeLeagueFromMyFavouritesPayload, removeLeagueFromDraft);
		},
		onSettled(data) {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		},
	});
	return mutation;
}

export const useAddMatchToMyFavourites = () => {
	const queryClient = useQueryClient();
	const { updateAuthToken } = useAuthActions();
	const apiDomain = useSiteStore(state => state.apiDomain);
	const mutation = useMutation({
		mutationFn: (addMatchToMyFavouritesPayload: AddMatchToMyFavouritesPayload) => updateMyFavourite(apiDomain, MyFavouritePath.AddMatch, addMatchToMyFavouritesPayload),
		onSuccess(_, addMatchToMyFavouritesPayload) {
			handleMyFavouriteChange(queryClient, addMatchToMyFavouritesPayload, addMatchToDraft);
		},
		onSettled(data) {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		},
	});
	return mutation;
}

export const useRemoveMatchFromMyFavourites = () => {
	const queryClient = useQueryClient();
	const { updateAuthToken } = useAuthActions();
	const apiDomain = useSiteStore(state => state.apiDomain);
	const mutation = useMutation({
		mutationFn: (removeMatchFromMyFavouritesPayload: RemoveMatchFromMyFavouritesPayload) => updateMyFavourite(apiDomain, MyFavouritePath.RemoveMatch, removeMatchFromMyFavouritesPayload),
		onSuccess(_, removeMatchFromMyFavouritesPayload) {
			handleMyFavouriteChange(queryClient, removeMatchFromMyFavouritesPayload, removeMatchFromDraft);
		},
		onSettled(data) {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		},
	});
	return mutation;
}



const addLeagueToDraft: changeMyFavouriteAction<AddLeagueToMyFavouritesPayload> = (draft: MyFavouritesInfo) => (addLeagueToMyFavouritesPayload: AddLeagueToMyFavouritesPayload) => {
	const addLeagueData: MyFavouritesLeague = {
		LeagueId: addLeagueToMyFavouritesPayload.LeagueId,
		IsOutright: addLeagueToMyFavouritesPayload.SportType === 999,
		IsRunning: 0,
		SportType: addLeagueToMyFavouritesPayload.SportType
	}
	itemsEmptyCheck(draft.Ldata ?? [])
		.fold(
			() => draft.Ldata = [addLeagueData],
			() => draft.Ldata.push(addLeagueData)
		)
}

const removeLeagueFromDraft: changeMyFavouriteAction<RemoveLeagueFromMyFavouritesPayload> = (draft: MyFavouritesInfo) => (removeLeagueFromMyFavouritesPayload: RemoveLeagueFromMyFavouritesPayload) => {
	const removeIndex = draft.Ldata.findIndex(leagueData => leagueData.LeagueId === removeLeagueFromMyFavouritesPayload.LeagueId);
	draft.Ldata.splice(removeIndex, 1);
}


const addMatchToDraft: changeMyFavouriteAction<AddMatchToMyFavouritesPayload> = (draft: MyFavouritesInfo) => (addMatchToMyFavouritesPayload: AddMatchToMyFavouritesPayload) => {
	const addMatchData: MyFavouritesMatch = {
		MatchId: addMatchToMyFavouritesPayload.MatchId,
		ExpireTime: addMatchToMyFavouritesPayload.Time,
		IsRunning: 0,
		SportType: addMatchToMyFavouritesPayload.SportType
	}
	itemsEmptyCheck(draft.Mdata ?? [])
		.fold(
			() => draft.Mdata = [addMatchData],
			() => draft.Mdata.push(addMatchData)
		)
}

const removeMatchFromDraft: changeMyFavouriteAction<RemoveMatchFromMyFavouritesPayload> = (draft: MyFavouritesInfo) => (removeMatchFromMyFavouritesPayload: RemoveMatchFromMyFavouritesPayload) => {
	const removeIndex = draft.Mdata.findIndex(matchData => matchData.MatchId === removeMatchFromMyFavouritesPayload.MatchId);
	draft.Mdata.splice(removeIndex, 1);
}

export const fetchMyFavouritesStatus = async (apiDomain: ApiDomain, data: MyFavouritesStatus): Promise<ApiQueryPromise<MyFavouritesInfo>> => {
	const authToken = getAuthToken();
	const apiUrl = createApiUrl(apiDomain, MyFavouritePath.GetStatus);
	const response = await fetchComposer.postWithBearer(
		apiUrl.toString(),
		{
			body: JSON.stringify(data)
		}
	)(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`, { cause: response });
	}
	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
}

export const useMyFavouritesStatus = (myFavouritesdata: MyFavouritesStatus) => {
	const apiDomain = useSiteStore(state => state.apiDomain);
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.MY_FAVOURITE_STATUS],
		queryFn: () => fetchMyFavouritesStatus(apiDomain, myFavouritesdata),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: myFavouritesdata !== undefined
			&& !!apiDomain.ctcdDomain
			&& (myFavouritesdata.leagueIds && myFavouritesdata.leagueIds?.length > 0 || myFavouritesdata.matchIds?.length > 0)
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};
		updateJwt();
	}, [data?.jwtToken]);


	return {
		data: data?.data?.Data,
		isLoading,
		isError,
		error,
		refetch
	}
}

export interface ShowAllOddsForMultiSportRequest {
	sportIds: number[],
	leagueIds?: number[],
	matchIds?: number[]
}

export const fetchShowAllOddsForMultiSport = async (req: ShowAllOddsForMultiSportRequest): Promise<SportMatchesData | undefined> => {
	const payloadFormData = getFormData({
		DateType: 'fav',
		BetTypeClass: 'OU',
		GameType: 0
	});

	req.sportIds.forEach(sportId => {
		payloadFormData.append('GameIds', String(sportId));
	});
	req.leagueIds?.forEach(leagueId => {
		payloadFormData.append('LeagueIds', String(leagueId));
	});
	req.matchIds?.forEach(matchId => {
		payloadFormData.append('MatchIds', String(matchId));
	});

	const apiPath = 'ShowAllOddsForMultiSport';
	const oddsService = OddsService.getInstance();
	const token = oddsService.accessOption.token;
	const oddsServerApiUrl = oddsService.getOddsServerUrl(apiPath);
	const response = await jwtFetcher<ShowAllOddsResponse>(oddsServerApiUrl, {
		method: 'POST',
		headers: {
			'_mculture': oddsService.oddsLocale,
		},
		body: payloadFormData,
	})(token);

	if (response.ErrorCode !== 0) {
		throw new Error(`${response.ErrorCode}: ${response.ErrorMsg}`);
	}
	return response.Data;
}

//add for promotion before odds render issue 
export const fetchShowAllOddsForPromtionMultiSport = async (
	oddsServerUrl: string, 
	token: string,
	req: ShowAllOddsForMultiSportRequest): Promise<SportMatchesData | undefined> => {
	const payloadFormData = getFormData({
		DateType: 'fav',
		BetTypeClass: 'OU',
		GameType: 0
	});

	req.sportIds.forEach(sportId => {
		payloadFormData.append('GameIds', String(sportId));
	});
	req.matchIds?.forEach(matchId => {
		payloadFormData.append('MatchIds', String(matchId));
	});

	const oddsService = OddsService.getInstance();
	
	const response = await jwtFetcher<ShowAllOddsResponse>(oddsServerUrl, {
		method: 'POST',
		headers: {
			'_mculture': oddsService.oddsLocale,
		},
		body: payloadFormData,
	})(token);

	if (response.ErrorCode !== 0) {
		throw new Error(`${response.ErrorCode}: ${response.ErrorMsg}`);
	}
	return response.Data;
}